import React from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Save, X, Loader2, CircleHelp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CategoryResult } from "@/types/supabase";
import { IconSelector } from "./IconSelector";
import iconMap from "@/components/icons/iconMap";

interface CategoryRowProps {
  category: CategoryResult;
  isEditing: boolean;
  editCategoryName: string;
  editCategoryIcon: string;
  updateMutation: {
    isPending: boolean;
    mutate: (data: { id: string; data: { name?: string; icon?: string } }) => void;
  };
  onEditClick: (category: CategoryResult) => void;
  onDeleteClick: (category: CategoryResult) => void;
  setIsEditing: (id: string | null) => void;
  setEditCategoryName: (name: string) => void;
  setEditCategoryIcon: (icon: string) => void;
}

const CategoryRow = ({
  category,
  isEditing,
  editCategoryName,
  editCategoryIcon,
  updateMutation,
  onEditClick,
  onDeleteClick,
  setIsEditing,
  setEditCategoryName,
  setEditCategoryIcon,
}: CategoryRowProps) => {
  const renderIcon = (iconName: string | null) => {
    if (!iconName || typeof iconName !== 'string') {
      return <span className="text-gray-400 text-sm">No icon</span>;
    }

  const Icon = iconMap[iconName as keyof typeof iconMap];
    return Icon ? (
      <Icon className="h-5 w-5" />
    ) : (
      <CircleHelp className="h-5 w-5 text-orange-500" />
    );
  };

  const handleUpdateCategory = (id: string) => {
    if (!editCategoryName.trim()) return;

    const updates: { name?: string; icon?: string } = {
      name: editCategoryName.trim(),
    };
    
    if (editCategoryIcon.trim()) {
      updates.icon = editCategoryIcon.trim();
    }

    updateMutation.mutate({ id, data: updates });
  };

  if (isEditing) {
    return (
      <TableRow>
        <TableCell>
          <Input
            value={editCategoryName}
            onChange={(e) => setEditCategoryName(e.target.value)}
            placeholder="Category name"
          />
        </TableCell>
        <TableCell>
          <div className="w-64">
            <IconSelector
              value={editCategoryIcon}
              onChange={setEditCategoryIcon}
              placeholder="Choose an icon"
            />
          </div>
        </TableCell>
        <TableCell className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleUpdateCategory(category.id)}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsEditing(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="font-medium">{category.name}</TableCell>
      <TableCell>
        {renderIcon(category.icon ?? null)}
      </TableCell>
      <TableCell className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onEditClick(category)}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onDeleteClick(category)}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default CategoryRow;
